import { ArrowDownTrayIcon, WrenchScrewdriverIcon } from '@heroicons/react/20/solid';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { SoftwareType } from '../../../@types/Company';
import { SOFTWARE_CONFIGS  } from '../../../@types/Company';
import { IMPORT_SOURCES } from '../../../@types/ImportSource';
import Spinner from '../../common/Spinner';

// Navigation state from PendingConfigImports
interface ImportNavigationState {
  companyId: number;
  companyName: string;
  companySiren: string;
  selectedSoftware: SoftwareType | null;
}

// Lazy load the import components for code splitting
const ImmOpenImport = lazy(() => import('../ImmOpenImport'));
const NeoteemImport = lazy(() => import('../NeoteemImport'));
const RnicExcelImport = lazy(() => import('../RnicExcelImport'));

// Loading fallback component
const TabLoadingFallback: React.FC = () => (
  <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
    <div className="flex items-center justify-center py-16">
      <Spinner size="lg" />
      <span className="ml-4 text-gray-600">Chargement du module d'import...</span>
    </div>
  </div>
);

// Not implemented placeholder component
interface NotImplementedPlaceholderProps {
  displayName: string;
}

const NotImplementedPlaceholder: React.FC<NotImplementedPlaceholderProps> = ({ displayName }) => (
  <div className="mb-8 rounded-lg bg-white p-8 shadow-md">
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <WrenchScrewdriverIcon className="mb-6 h-24 w-24 text-amber-400" />
      <h2 className="mb-4 text-2xl font-bold text-gray-800">
        Développement en cours
      </h2>
      <p className="mb-6 max-w-lg text-gray-600">
        L'import pour le logiciel
        {' '}
        <span className="font-semibold text-rgpd-blue-dark">{displayName}</span>
        {' '}
        est en cours de développement.
      </p>
    </div>
  </div>
);

// Props interface for import components (ImmOpenImport, NeoteemImport)
export interface SoftwareImportComponentProps {
  initialCompany?: {
    id: number;
    name: string;
    siren: string;
  } | null;
}

const SoftwareImport: React.FC = () => {
  const location = useLocation();
  const navigationState = location.state as ImportNavigationState | null;
  
  // Determine initial tab: use navigation state if provided, otherwise default to IMMOPEN.
  const getInitialTab = (): SoftwareType => {
    if (navigationState?.selectedSoftware) {
      const config = SOFTWARE_CONFIGS.find((c) => c.id === navigationState.selectedSoftware);
      if (config?.implemented) {
        return navigationState.selectedSoftware;
      }
    }
    return IMPORT_SOURCES.IMMOPEN;
  };
  
  const [activeTab, setActiveTab] = useState<SoftwareType>(getInitialTab);
  
  // Initial company from navigation state
  const initialCompany = navigationState ? {
    id: navigationState.companyId,
    name: navigationState.companyName,
    siren: navigationState.companySiren,
  } : null;
  
  // Clear navigation state after reading to avoid persisting on refresh
  useEffect(() => {
    if (navigationState) {
      window.history.replaceState({}, document.title);
    }
  }, []);

  const currentConfig = SOFTWARE_CONFIGS.find((config) => config.id === activeTab)!;

  const handleTabChange = (tabId: SoftwareType) => {
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    if (!currentConfig.implemented) {
      return <NotImplementedPlaceholder displayName={currentConfig.displayName} />;
    }

    // Only pass initialCompany if the current tab matches the navigation state's software
    const shouldPassInitialCompany = navigationState?.selectedSoftware === activeTab;
    const propsToPass: SoftwareImportComponentProps = shouldPassInitialCompany ? { initialCompany } : {};

    switch (activeTab) {
      case IMPORT_SOURCES.RNIC_EXCEL:
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <RnicExcelImport {...propsToPass} />
          </Suspense>
        );
      case IMPORT_SOURCES.IMMOPEN:
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <ImmOpenImport {...propsToPass} />
          </Suspense>
        );
      case IMPORT_SOURCES.NEOTEEM:
        return (
          <Suspense fallback={<TabLoadingFallback />}>
            <NeoteemImport {...propsToPass} />
          </Suspense>
        );
      default:
        return <NotImplementedPlaceholder displayName={currentConfig.displayName} />;
    }
  };

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <ArrowDownTrayIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Import Logiciel Métier
        </h1>
        <p className="text-gray-600">
          Importez le fichier d'export de votre logiciel métier pour mettre à jour les dates d'AG et créer les copropriétés manquantes.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100 md:grid-cols-3 lg:grid-cols-6">
          {SOFTWARE_CONFIGS.map((config) => {
            const isActive = activeTab === config.id;
            return (
              <button
                key={config.id}
                onClick={() => handleTabChange(config.id)}
                className={`relative flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 px-4 py-5 transition-all duration-300 ${
                  isActive
                    ? 'border-b-4 border-rgpd-blue-dark bg-white font-bold text-slate-800 shadow-sm'
                    : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500 hover:bg-gray-50'
                }`}
              >
                <ArrowDownTrayIcon className="h-6 w-6" />
                <span className="text-center text-sm leading-relaxed">{config.displayName}</span>
                {!config.implemented && (
                  <span className="absolute top-2 right-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                    Bientôt
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default SoftwareImport;
